import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, Banknote } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { PaymentRunCreateDrawer } from '../../components/PaymentCreateDrawer';
import { PaymentRunDrawer } from '../../components/PaymentRunDrawer';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { api } from '../../lib/api';
import { formatAmount, formatDate } from '../../lib/format';
import { useCan } from '../../hooks/useCan';

interface PaymentRun {
  id: string;
  status: 'draft' | 'scheduled' | 'executing' | 'complete' | 'failed';
  rail: 'zengin' | 'swift' | 'ach' | 'sepa' | 'wise' | 'manual';
  base_currency: string;
  total_base_cents: string;
  scheduled_for: string | null;
  executed_at: string | null;
  zengin_file_url: string | null;
  created_at: string;
}

const STATUS_TONE: Record<PaymentRun['status'], 'neutral' | 'accent' | 'success' | 'warn'> = {
  draft: 'neutral',
  scheduled: 'accent',
  executing: 'accent',
  complete: 'success',
  failed: 'warn',
};

export function PaymentsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const { data: runs, isLoading, isError, refetch } = useQuery<PaymentRun[]>({
    queryKey: ['payment-runs', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/payment-runs`)).data,
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="display text-2xl font-semibold">{t('payments.title')}</h1>
          {canMutate && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
            >
              <Plus size={14} />
              {t('payments.createRun')}
            </button>
          )}
        </div>
        <IntroCallout
          storageKey="payments"
          title={t('intro.payments.title')}
          body={t('intro.payments.body')}
        />
        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : isError ? (
            <QueryError onRetry={refetch} />
          ) : !runs || runs.length === 0 ? (
            <EmptyState
              icon={Banknote}
              title={t('emptyState.payments.title')}
              description={t('emptyState.payments.body')}
              primaryLabel={t('payments.createRun')}
              primaryIcon={Plus}
              onPrimary={() => setCreateOpen(true)}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {runs.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setDrawerId(r.id)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                  >
                  <div>
                    <div className="font-medium">{t(`payments.rails.${r.rail}`)}</div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {r.executed_at
                        ? `${t('payments.executedAt')} ${formatDate(r.executed_at, i18n.language)}`
                        : r.scheduled_for
                          ? `${t('payments.scheduledFor')} ${formatDate(r.scheduled_for, i18n.language)}`
                          : t('common.draft')}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Pill tone={STATUS_TONE[r.status]}>{t(`common.${r.status}`)}</Pill>
                    <span className="num font-medium">
                      {formatAmount(r.total_base_cents, r.base_currency, i18n.language)}
                    </span>
                  </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <PaymentRunCreateDrawer wsId={wsId} open={createOpen} onOpenChange={setCreateOpen} />
          <PaymentRunDrawer
            wsId={wsId}
            runId={drawerId}
            open={!!drawerId}
            onOpenChange={(o) => !o && setDrawerId(null)}
          />
        </>
      )}
    </AppShell>
  );
}
